<template>
  <header class="nav-bar">
    <div class="nav-left">
      <img src="@/assets/logo/dot_1.png" alt="Dot Inc 로고" class="nav-logo" />
    </div>

    <nav class="nav-center">
      <a href="https://google.apps-dotincorp.com/">Home</a>
      <a href="https://google.apps-dotincorp.com/canvas">Dot Canvas</a>
      <RouterLink to="/" :class="{ active: isDotStemActive }" class="nav-item">
        <span class="nav-label">
          Dot Stem
          <img src="@/assets/img/beta.svg" class="beta-top" />
        </span>
      </RouterLink>

      <a href="https://cane.google.apps-dotincorp.com" class="nav-item">
        <span class="nav-label">
          Dot Explore
          <img src="@/assets/img/beta.svg" class="beta-top" />
        </span>
      </a>

      <a href="https://google.apps-dotincorp.com/cloud">Dot Cloud</a>
    </nav>

    <div class="nav-right">
      <!-- 언어 선택 -->
      <div class="dropdown-wrapper" @focusout="handleLanguegeFocusOut">
        <button
          class="nav-icon-btn"
          @click="toggleLanguageDropdown"
          aria-label="select language"
        >
          <img src="@/assets/img/Language.svg" alt="" />
        </button>

        <div v-if="isLanguageOpen" class="dropdown-menu" role="menu">
          <div
            v-for="lang in supportedLanguages"
            :key="lang.code"
            class="dropdown-item"
            role="menuitem"
            tabindex="0"
            :aria-label="`select language ${lang.name}`"
            @click="selectLanguage(lang.code)"
            @keyup.enter.prevent="selectLanguage(lang.code)"
          >
            <span class="check-mark">
              {{ lang.code === currentLanguage ? "✓" : "" }}
            </span>
            <span class="lang-name">{{ lang.name }}</span>
          </div>
        </div>
      </div>

      <!-- DotPad -->
      <button
        class="nav-icon-btn"
        @click="$emit('open-device')"
        aria-label="open device connection dialog"
      >
        <img src="@/assets/img/DotPad.svg" alt="" />
      </button>

      <!-- 로그인 안 된 경우 -->
      <button
        v-if="!isLoggedIn"
        class="login-btn"
        @click="goToLogin"
        aria-label="login to your account"
      >
        Login
      </button>

      <!-- 로그인 된 경우 -->
      <div v-else class="dropdown-wrapper" @focusout="handleProfileFocusOut">
        <button
          class="nav-icon-btn"
          @click="toggleProfileDropdown"
          aria-label="profile options"
        >
          <img src="@/assets/img/Profile.svg" alt="" />
        </button>

        <div v-if="isProfileOpen" class="dropdown-menu" role="menu">
          <div
            class="dropdown-item"
            role="menuitem"
            @click="logout"
            aria-label="logout from your account"
            tabindex="0"
            @keyup.enter.prevent="logout"
          >
            로그아웃
          </div>
        </div>
      </div>
    </div>
  </header>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

export default {
  name: "NavBar",
  emits: ["open-device"],

  data() {
    return {
      isLanguageOpen: false,
      isProfileOpen: false,
    };
  },

  computed: {
    ...mapGetters(["currentLanguage", "supportedLanguages", "getUserNo"]),
    isLoggedIn() {
      return !!this.getUserNo;
    },
    isDotStemActive() {
      return ["HomePage", "dotCanvas", "ImageDetail"].includes(
        this.$route.name,
      );
    },
    loginUrl() {
      const isLocal =
        window.location.hostname === "local.google.apps-dotincorp.com" ||
        window.location.hostname === "localhost";

      const callbackUrl = isLocal
        ? "https://local.google.apps-dotincorp.com:3000/login-callback"
        : "https://stem.google.apps-dotincorp.com/login-callback";

      return `https://account.google.apps-dotincorp.com/login?cb=${callbackUrl}&s=S260129001&c=C251117001`;
    },
  },

  methods: {
    ...mapActions(["setLanguage"]),

    goToLogin() {
      window.location.href = this.loginUrl;
    },

    handleLanguegeFocusOut(e) {
      if (!e.currentTarget.contains(e.relatedTarget)) {
        this.isLanguageOpen = false;
      }
    },

    handleProfileFocusOut(e) {
      if (!e.currentTarget.contains(e.relatedTarget)) {
        this.isProfileOpen = false;
      }
    },

    toggleLanguageDropdown() {
      this.isLanguageOpen = !this.isLanguageOpen;
      if (this.isLanguageOpen) {
        this.$nextTick(() => {
          const items = this.$el.querySelectorAll(".dropdown-item .lang-name");
          if (items.length) items[0].focus();
        });
      }
    },

    toggleProfileDropdown() {
      this.isProfileOpen = !this.isProfileOpen;
    },

    selectLanguage(langCode) {
      this.setLanguage(langCode);
      this.isLanguageOpen = false;
    },

    logout() {
      fetch("/user-app/v2/auth/logout", {
        method: "DELETE",
        credentials: "include",
      }).finally(() => {
        // ✅ store 초기화 (localStorage 자동 삭제)
        this.$store.commit("SET_USER_NO", null);
        this.isProfileOpen = false;
        this.$router.replace("/");
      });
    },

    handleClickOutside(e) {
      if (!this.$el.contains(e.target)) {
        this.isLanguageOpen = false;
      }
    },
  },

  mounted() {
    document.addEventListener("click", this.handleClickOutside);
  },

  beforeUnmount() {
    document.removeEventListener("click", this.handleClickOutside);
  },
};
</script>

<style scoped>
.nav-bar {
  height: 85px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 40px;
  border-bottom: 1px solid #e5e7eb;
  background: #fff;
}

.nav-center a {
  margin: 0 20px;
  color: #4b5563;
  text-decoration: none;
  font-weight: 600;
  font-size: 21px;
}

.nav-center a.router-link-active {
  color: #f97316;
  font-weight: 750;
}

.nav-logo {
  height: 40px;
  object-fit: contain;
  cursor: pointer;
}

.nav-right {
  display: flex;
  align-items: center;
  gap: 18px;
}

.nav-icon-btn {
  all: unset;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  height: 60px;
  padding: 0 8px;
  border-radius: 8px;

  transition: background 0.15s ease, transform 0.15s ease;
}

.nav-icon-btn img {
  height: 38px;
  width: auto;
  opacity: 0.7;
  transition: opacity 0.15s ease;
}

.nav-icon-btn:focus-visible {
  transform: scale(0.95);
  background: #f3f4f6;
}

/* hover 효과 */
.nav-icon-btn:hover {
  background: #f3f4f6;
}

.nav-icon-btn:hover img {
  opacity: 1;
}

/* 클릭 느낌 */
.nav-icon-btn:active {
  transform: scale(0.95);
}

/* 드롭다운 */
.dropdown-wrapper {
  position: relative;
}

.dropdown-menu {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 4px;
  min-width: 160px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  overflow: hidden;
}

.dropdown-item {
  display: flex;
  align-items: center;
  padding: 10px 14px;
  cursor: pointer;
  font-size: 14px;
  color: #374151;
  transition: background 0.15s ease;
}

.dropdown-item:hover {
  background: #f3f4f6;
}

.check-mark {
  width: 20px;
  font-size: 14px;
  color: #374151;
}

.lang-name {
  flex: 1;
}

.login-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;

  background: #f45a22;
  color: white;
  text-decoration: none;
  border: none;

  padding: 10px 20px;
  border-radius: 40px;

  font-size: 16px;
  font-weight: 700;

  transition: all 0.2s ease;
}

.login-btn:focus-visible {
  outline: none;
  background: #e24f1b;
  transform: translateY(-1px);
}

.login-btn:hover {
  background: #e24f1b;
  transform: translateY(-1px);
}

.login-btn:active {
  transform: scale(0.96);
}
.nav-center .active {
  color: #f97316;
  font-weight: 750;
}

.nav-item {
  position: relative;
  display: inline-block;
}

.nav-label {
  position: relative;
  display: inline-block;
}

.beta-top {
  position: absolute;
  top: -5px;
  right: 0; /* 👈 오른쪽 끝 기준 */
  transform: translateX(0%); /* 살짝 오른쪽으로 밀기 */
  width: 50px;
  height: auto;
  pointer-events: none;
}
</style>
